<script setup>
import {reactive, watch, computed, nextTick, onMounted, ref} from 'vue';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {Permissions} from "@/Utils/Permissions";
import {FormValidation} from "@/Utils/FormValidation";
import CreateUserFromPersonComponent from "@/Components/UserManagement/CreateUserFromPersonComponent.vue";
import {ToastUtil} from "@/Utils/ToastUtil";
import {DateUtil} from "@/Utils/DateUtil";
import {userStore} from "@/Stores/UserStore";


const compName = 'PersonComponent';

const data = reactive({
  temporaryPasswordShow: false,
  personId: 0,
  dialogStatus: 'closed',
  dataLoading: true,
  date: null,
  dateFormatted: '',
  temporaryPassword: '',
  adManagedUser: false,
  createAccount: false,
  roles: [],
  permissions: [],
  editedItem: {
    name: '',
    streetaddress: '',
    city: '',
    state: '',
    zip: '',
    mobilephone: '',
    homephone: '',
    email: '',
    driverlicensenumber: '',
    dob: null,
    company: ''
  },
  defaultItem: {
    name: '',
    streetaddress: '',
    city: '',
    state: '',
    zip: '',
    mobilephone: '',
    homephone: '',
    email: '',
    driverlicensenumber: '',
    dob: null,
    company: ''
  },
  roleList: [],
  headers: [
    {
      title: 'Name',
      align: 'start',
      sortable: true,
      key: 'name',
    },
    { title: 'Address', key: 'streetaddress', sortable: true },
    { title: 'City', key: 'city', sortable: true },
    { title: 'State', key: 'state', sortable: true },
    { title: 'Zip', key: 'zip', sortable: true },
    { title: 'Mobile Phone', key: 'mobilephone', sortable: true },
    { title: 'Home Phone', key: 'homephone', sortable: true },
    { title: 'Email', key: 'email', sortable: true },
    { title: "Driver's License", key: 'driverlicensenumber', sortable: true },
    { title: 'DOB', key: 'dob', sortable: true },
    { title: 'Company', key: 'company', sortable: true },
    { title: 'Roles', key: 'roles', sortable: false},
    { title: 'User Account', key: 'hasaccount', sortable: true },
    { title: 'Actions', key: 'actions', sortable: false },
  ],
  isEditing: false,
  loading: false,
  menu: false,
  openPersonForm: false,
  person: [],
  search: '',
  states: [
    'AL',
    'AK',
    'AS',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'DC',
    'FM',
    'FL',
    'GA',
    'GU',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MH',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'MP',
    'OH',
    'OK',
    'OR',
    'PW',
    'PA',
    'PR',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VI',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ],
  valid: true
});

const form = ref(null);

watch(
    ()=>data.date,
    (newDate)=>{
      data.dateFormatted = formatDate(newDate);
    }
)

const csvData = computed(()=>{
  // CSV File
  return data.person.map((item) => ({
    ...item,
    name: item.name,
    streetaddress: item.streetaddress,
    city: item.city,
    state: item.state,
    zip: item.zip,
    phone: item.homephone,
    mobile: item.mobilephone,
    email: item.email,
    driverlicensenumber: item.driverlicensenumber,
    dob: item.dob,
    company: item.company,
  }))
});

const csvExport = (arrData) => {
  // CSV File
  let csvContent = 'data:text/csv;charset=utf-8,'
  csvContent += [
    Object.keys(arrData[0]).join(';'),
    ...arrData.map((item) => Object.values(item).join(';')),
  ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')

  const data = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', data)
  link.setAttribute('download', 'export.csv')
  link.click()
}

const acceptHomeNumber = () => {
  let x = data.editedItem.homephone.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  data.editedItem.homephone = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
}

const acceptMobileNumber = () => {
  let x = data.editedItem.mobilephone.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  data.editedItem.mobilephone = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
}

const formatDate = (date) => {
  let dob = DateUtil.formatDate(date);

  return dob ? dob : 'No DOB entered';
}

const listAll = () => {
  ApiWrapper.get('person', 'list-persons').then(async (response) => {
      data.person = response.people;
      for(let x in data.person){
        data.person[x].formattedDate = formatDate(data.person[x].dob)
      }

    await ApiWrapper.get('auth-roles', 'list-roles')
    .then((response) => {
      const isAdmin = userStore.userInfo?.isAdmin || false;
      data.roleList = response.roles.map((role) => {
        // If the user is not an admin and does not have a given role you shouldn't be able to assign/remove it
        if(!isAdmin && !userStore.userInfo?.roleIds.includes(role.id)) {
          role.props = { disabled: true};
        }
        // Add an * by default roles
        return {
          ...role,
          role: role.is_default ? `${role.role}` : role.role
        };
      });
    })
    .catch((err) => {
      ToastUtil.error(err);
    })
    .finally(()=>{
      data.loading = false
      data.dataLoading = false
    })
  })
  .catch((e)=>{
    ToastUtil.error(e);
    data.loading = false
    data.dataLoading = false
  });
}

// Clear out the new user data
const clearData = () => {
  data.temporaryPassword = '';
  data.adManagedUser = false;
  data.createAccount = false;
  data.roles = [];
  data.permissions = [];
}

const createUserFromPerson = (item) => {
  data.dialogStatus = 'open';
  data.personId = item.id;
}

const editItem = (item) => {
  clearData();

  data.isEditing = true
  data.editedItem = JSON.parse(JSON.stringify(item))
  // if(data.editedItem.dob){
  //   data.date = data.editedItem.dob
  // }
  data.dateFormatted = formatDate(data.editedItem.dob)
  data.openPersonForm = true
}

// const deleteItem = (item) => {
//   let deleteMessage = confirm(
//     'Are you sure you want to delete this item?'
//   )
//   if (deleteMessage === true) {
//     data.loading = true
//     ApiWrapper.post('person', 'delete-person', { item: item })
//       .then((response) => {
//         listAll()
//          ToastUtil.successMessage('Deleted person');
//       })
//      .catch((e) => {
//        ToastUtil.error(e);
//      });
//   }
// }

const formatPhoneNumber = (phoneNumberString) => {
  let cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }
  return null
}

const close = () => {
  data.openPersonForm = false
  nextTick(() => {
    data.editedItem = Object.assign({}, data.defaultItem)
    data.dateFormatted = ''
    data.menu = false
    data.isEditing = false
  })
}

const save = () => {
  if(data.createAccount && data.roles.length === 0) {
    ToastUtil.errorMsg('Users must have at least one role selected');
    return;
  }

  if (form.value.validate()) {
    data.loading = true
    if(data.date instanceof Date){
      data.editedItem.dob = data.date.getFullYear()+"-"+(data.date.getMonth()+1)+"-"+data.date.getDate();
    }
    if (data.isEditing) {
      ApiWrapper.post('person', 'edit-person', {
            editedItem: data.editedItem,
          })
          .then((response) => {
            listAll()
            activeComponentStore.notifyComponents(['person'],compName);
            ToastUtil.successMsg('Updated person');
          })
          .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
          })
    } else {
      ApiWrapper.post('person', 'create-person', {
            editedItem: data.editedItem,
            createAccount: data.createAccount,
            temporaryPassword: data.temporaryPassword,
            roles: data.roles,
            permissions: data.permissions,
            adManagedUser: data.adManagedUser
          })
          .then((response) => {
            listAll()
            activeComponentStore.notifyComponents(['person'],compName);
            ToastUtil.successMsg('Created person');
          })
          .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
          })
    }
    close()
  }
}

const validatePassword = (password) => {
  // Regular expression to check for:
  // ^(?=.*?[A-Z])     - At least one uppercase letter
  // (?=.*?[a-z])     - At least one lowercase letter
  // (?=.*?[0-9])     - At least one digit
  // (?=.*?[#?!@$%^&*-]) - At least one special character (adjust allowed characters as needed)
  // .{8,}$          - Minimum 8 characters in length
  const passwordRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;
  return passwordRegex.test(password);
}

onMounted(()=>{
  listAll();
})

</script>

<template>
  <v-container fluid>
    <CreateUserFromPersonComponent :personId="data.personId" :status="data.dialogStatus" @closed="data.dialogStatus='closed'" @newUser="listAll()"/>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Person</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-icon @click="csvExport(csvData)" v-if="!data.openPersonForm"> mdi-file-download </v-icon>

        <v-spacer></v-spacer>

        <!-- New Person Button -->
        <v-btn
            v-if="!data.openPersonForm && Permissions.checkPermissions(['users.create'])"
            variant="elevated"
            class="primary"
            size="small"
            @click="data.openPersonForm = true; clearData();"
        >
          New Person
        </v-btn>
      </v-toolbar>

      <!-- New Person Form -->
      <div v-if="data.openPersonForm">
        <v-card flat>
          <v-list>
            <v-list-item>
                <v-list-item-title v-if="!data.isEditing" class="primary--text"
                >New Person</v-list-item-title
                >
                <v-list-item-title v-else class="primary--text"
                >Edit Person</v-list-item-title
                >
            </v-list-item>
          </v-list>

          <v-alert
              v-if="data.editedItem.aduser"
              density="compact"
              text="This is an Active Directory managed user. Their name and email can only be edited within Active Directory."
              type="warning"
              class="ma-4"
          ></v-alert>

          <v-form ref="form" v-model="data.valid" lazy-validation>
            <v-list>
              <v-list-item>
                <v-row>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.name"
                        label="Name"
                        :rules="[v => !!v || 'Name is required']"
                        :disabled="data.editedItem.aduser"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.streetaddress"
                        label="Address"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.city"
                        label="City"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-autocomplete
                        v-model="data.editedItem.state"
                        :items="data.states"
                        label="State"
                        auto-select-first
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.zip"
                        label="Zip Code"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.homephone"
                        label="Home Phone"
                        :rules="[FormValidation.phoneRules]"
                        @input="acceptHomeNumber"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.mobilephone"
                        label="Mobile Phone"
                        :rules="[FormValidation.phoneRules]"
                        @input="acceptMobileNumber"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.email"
                        label="Email"
                        :disabled="data.editedItem.aduser"
                        :rules="[v => !!v || 'Email is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.driverlicensenumber"
                        label="Driver's License #"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-menu
                        ref="menu"
                        v-model="data.menu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                    >
                      <template v-slot:activator="{ on, props }">
                        <v-text-field
                            v-model="data.dateFormatted"
                            label="Date of Birth"
                            persistent-hint
                            prepend-inner-icon="mdi-calendar"
                            v-bind="props"
                            v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                          v-model="data.date"
                          scrollable
                          @input="data.menu = false"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="data.editedItem.company"
                        label="Company"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="9" v-if="!data.isEditing">
                    <v-row>
                      <v-col cols="12" sm="4">
                        <v-checkbox
                            class="mr-4"
                            label="Create Account"
                            v-model="data.createAccount">
                        </v-checkbox>

                        <v-checkbox
                            v-if="data.createAccount"
                            class="mr-4"
                            label="Active Directory User"
                            v-model="data.adManagedUser">
                        </v-checkbox>
                      </v-col>
                      <v-col cols="12" sm="4" v-if="data.createAccount">
                        <v-text-field
                            v-if="!data.adManagedUser"
                            v-model="data.temporaryPassword"
                            label="Temp Password"
                            :rules="[
                                v => !!v || 'Password is required',
                                v => !!validatePassword(v) || 'Password must be at least 8 characters and contain at least 1 of: Upper case, lower case, special character, digit'
                                ]"
                            :append-icon="data.temporaryPasswordShow ? 'mdi-eye' : 'mdi-eye-off'"
                            :type="data.temporaryPasswordShow ? 'text' : 'password'"
                            @click:append="data.temporaryPasswordShow = !data.temporaryPasswordShow"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" v-if="data.createAccount">
                        <v-select
                            v-model="data.roles"
                            :items="data.roleList"
                            label="Roles"
                            item-title="role"
                            item-value="role"
                            chips
                            multiple
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" sm="3" align="center">
                    <v-btn color="primary" @click="save" :disabled="!data.valid"
                    >Save</v-btn
                    >
                    <v-btn color="error" @click="close">Cancel</v-btn>
                  </v-col>
                </v-row>
              </v-list-item>
            </v-list>
          </v-form>
        </v-card>
      </div>

      <v-card-title>
        <v-text-field
            v-model="data.search"
            append-inner-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
        ></v-text-field>
      </v-card-title>
      <v-data-table
          item-value="id"
          :headers="data.headers"
          :items="data.person"
          :search="data.search"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <RouterLink :to="`/userprofile/${item.id}`">
                {{ item.name }}
              </RouterLink>
            </td>
            <td>
              <span>{{ item.address }}</span>
            </td>
            <td>
              <span>{{ item.city }}</span>
            </td>
            <td>
              <span>{{ item.state }}</span>
            </td>
            <td>
              <span>{{ item.zip }}</span>
            </td>
            <td>
              <span>{{ formatPhoneNumber(item.mobilephone) }}</span>
            </td>
            <td>
              <span>{{ formatPhoneNumber(item.homephone) }}</span>
            </td>
            <td>
              <span>{{ item.email }}</span>
            </td>
            <td>
              <span>{{ item.driverlicensenumber }}</span>
            </td>
            <td>
              <span v-if="item.formattedDate !== undefined">{{ item.formattedDate }}</span>
            </td>
            <td>
              <span>{{ item.company }}</span>
            </td>
            <td>
              <v-chip
                  v-if="item.is_admin === true"
                  prepend-icon="mdi-star"
                  class="ma-1"
                  size="small"
                  color="green"
              >
                AD Admin
              </v-chip>
              <template v-for="role in item.roles" :key="role">
                <v-chip
                    class="ma-1"
                    size="small"
                    color="blue"
                >
                  {{ role }}
                </v-chip>
              </template>
            </td>
            <td>
              <v-icon v-if="item.cognitouser" > mdi-check </v-icon>

              <v-icon v-if="!item.cognitouser && Permissions.checkPermissions(['users.create'])" class="mr-2" @click="createUserFromPerson(item)"> mdi-plus </v-icon>
            </td>
            <td>
              <v-icon v-if="Permissions.checkPermissions(['users.update'])" class="mr-2" @click="editItem(item)"> mdi-pencil </v-icon>
            </td>
          </tr>
        </template>


<!--        <template v-slot:item.name="{ item }">-->
<!--              <RouterLink :to="`/userprofile/${item.id}`">-->
<!--                {{ item.name}}-->
<!--              </RouterLink>-->
<!--        </template>-->
<!--        <template v-slot:item.mobilephone="{ item }">-->
<!--          <span>{{ formatPhoneNumber(item.mobilephone) }}</span>-->
<!--        </template>-->
<!--        <template v-slot:item.homephone="{ item }">-->
<!--          <span>{{ formatPhoneNumber(item.homephone) }}</span>-->
<!--        </template>-->
<!--        <template v-slot:item.dob="{ item }">-->
<!--          <span v-if="item.formattedDate !== undefined">{{ item.formattedDate }}</span>-->
<!--        </template>-->
<!--        <template v-slot:item.actions="{ item }">-->
<!--          <v-icon class="mr-2" @click="editItem(item)"> mdi-pencil </v-icon>-->
<!--          &lt;!&ndash; <v-icon @click="deleteItem(item)"> mdi-delete </v-icon> &ndash;&gt;-->
<!--        </template>-->
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
  [v-cloak] {
    display: none;
  }
</style>